import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import { DEFAULT_CYBER_FEE_STRUCTURE } from "../data/defaultCyberFeeStructure";
import { getCmsApiUrl, getCmsOriginForUploads } from "../services/cmsApi";
import { FaChevronRight, FaCheck, FaCopy, FaHome, FaInfoCircle, FaUniversity, FaBed, FaCalendarAlt, FaSearch, FaPrint, FaDownload, FaShareAlt, FaShieldAlt } from "react-icons/fa";

const sections = {
  hero: "hero",
  programs: "programs",
  schedule: "schedule",
  sidebar: "sidebar",
  hostel: "hostel",
  payment: "payment",
  contact: "contact",
};

const CURRENCY = "TZS";

const feeStructureApiUrl = () => `${getCmsApiUrl().replace(/\/$/, "")}/fee-structure`;

/** Normalize CMS fee-structure JSON (same shape as state.feeData) */
function parseFeeStructurePayload(data) {
  if (!data || data.error) return null;
  return {
    ...data,
    feePdfProgrammesUrl: data.feePdfProgrammesUrl ?? data.fee_pdf_programmes_url,
    feePdfCyberSecurityUrl: data.feePdfCyberSecurityUrl ?? data.fee_pdf_cyber_security_url,
    downloadPdfUrl: data.downloadPdfUrl ?? data.download_pdf_url,
    feePrograms: JSON.parse(data.feePrograms || "[]"),
    paymentSchedules: JSON.parse(data.paymentSchedules || "[]"),
    otherPayments: JSON.parse(data.otherPayments || "[]"),
    accountDetails: JSON.parse(data.accountDetails || "[]"),
    foreignRemittance: JSON.parse(data.foreignRemittance || "{}"),
    contactPhones: JSON.parse(data.contactPhones || "[]"),
  };
}

const validPath = (p) => p && typeof p === "string" && p.startsWith("/");

/** Prefer dedicated programmes slot; legacy downloadPdfUrl only if programmes slot empty */
function getProgrammesPdfPath(data) {
  if (!data) return null;
  if (validPath(data.feePdfProgrammesUrl)) return data.feePdfProgrammesUrl;
  if (validPath(data.downloadPdfUrl)) return data.downloadPdfUrl;
  return null;
}

function getCyberPdfPath(data) {
  if (!data || !validPath(data.feePdfCyberSecurityUrl)) return null;
  return data.feePdfCyberSecurityUrl;
}
const formatAmount = (num) => {
  if (num === "-" || num === null || num === undefined) return "-";
  const n = typeof num === "number" ? num : parseInt(String(num).replace(/[^0-9]/g, ""), 10);
  if (isNaN(n)) return String(num);
  return `${CURRENCY} ${n.toLocaleString("en-US")}`;
};

/** Card headings: title case; NTA levels as `NTA-{n}` (not `NTA L n`) */
function formatFeeCardTitle(text) {
  if (text == null || text === "") return text;
  let s = String(text).trim().toLowerCase();
  s = s.replace(/\(\s*nta\s+l\s*(\d+)\s*\)/gi, "(nta-$1)");
  s = s.replace(/\(\s*nta\s*-\s*(\d+)\s*\)/gi, "(nta-$1)");
  s = s.replace(/\bnta\s+l\s*(\d+)\b/gi, "nta-$1");
  const parts = s.split(/(\(nta-\d+\)|\bnta-\d+\b)/gi);
  return parts
    .map((part) => {
      const inParen = part.match(/^\(nta-(\d+)\)$/i);
      if (inParen) return `(NTA-${inParen[1]})`;
      const bare = part.match(/^nta-(\d+)$/i);
      if (bare) return `NTA-${bare[1]}`;
      return part.replace(/\b\w/g, (c) => c.toUpperCase());
    })
    .join("");
}

/** Fee line labels: lowercase */
function formatFeeListLabel(text) {
  if (text == null || text === "") return text;
  return String(text).trim().toLowerCase();
}

const highlight = (text, query) => {
  if (!query) return text;
  const parts = String(text).split(new RegExp(`(${query})`, "ig"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="bg-yellow-300/60 text-black rounded px-0.5">{part}</mark>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

const ProgramFeeBlock = ({ title, items, total, expanded, onToggle, query, compactMidDesktop }) => {
  const list = Array.isArray(items) ? items : [];
  const mid = compactMidDesktop
    ? "min-[768px]:max-[1023px]:!p-4 min-[1024px]:max-[1535px]:!p-4 min-[768px]:max-[1023px]:!rounded-xl min-[1024px]:max-[1535px]:!rounded-xl min-[768px]:max-[1023px]:!shadow-sm min-[1024px]:max-[1535px]:!shadow-sm"
    : "";
  const midIcon = compactMidDesktop
    ? "min-[768px]:max-[1023px]:!h-7 min-[1024px]:max-[1535px]:!h-7 min-[768px]:max-[1023px]:!w-7 min-[1024px]:max-[1535px]:!w-7 min-[768px]:max-[1023px]:!rounded-md min-[1024px]:max-[1535px]:!rounded-md min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs"
    : "";
  const midTitle = compactMidDesktop
    ? "min-[768px]:max-[1023px]:!text-base min-[1024px]:max-[1535px]:!text-base min-[768px]:max-[1023px]:!leading-tight min-[1024px]:max-[1535px]:!leading-tight"
    : "";
  const midBtn = compactMidDesktop
    ? "min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1 min-[1024px]:max-[1535px]:!py-1 min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px]"
    : "";
  const midRow = compactMidDesktop
    ? "min-[768px]:max-[1023px]:!rounded-lg min-[1024px]:max-[1535px]:!rounded-lg min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5"
    : "";
  const midTotal = compactMidDesktop ? "min-[768px]:max-[1023px]:!mt-3 min-[1024px]:max-[1535px]:!mt-3 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs" : "";

  return (
    <div
      className={`relative flex flex-col rounded-2xl p-5 md:p-6 bg-white border border-gray-200 shadow-md transition-all duration-500 h-full [font-family:var(--cms-font-family)] ${mid}`}
      style={{ fontSize: "var(--cms-font-size)" }}
    >
      <div className={`mb-3 flex flex-col gap-2.5 ${compactMidDesktop ? "min-[768px]:max-[1023px]:!mb-2 min-[1024px]:max-[1535px]:!mb-2 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5" : ""}`}>
        <div className={`flex min-w-0 items-start gap-2.5 ${compactMidDesktop ? "min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5" : ""}`}>
          <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-custom-pink text-white ${midIcon}`}>
            $
          </span>
          <h3 className={`min-w-0 flex-1 text-balance break-normal text-lg font-bold normal-case leading-snug text-gray-800 md:text-xl ${midTitle}`}>
            {highlight(formatFeeCardTitle(title), query)}
          </h3>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onToggle}
            className={`max-w-full shrink-0 rounded-full border border-gray-200 bg-gray-100 px-3 py-1.5 text-center text-xs text-gray-700 transition-colors hover:bg-custom-pink hover:text-white ${midBtn}`}
          >
            {expanded ? "Hide breakdown" : "View breakdown"}
          </button>
        </div>
      </div>
      <div className={`space-y-2.5 ${compactMidDesktop ? "min-[768px]:max-[1023px]:!space-y-1.5 min-[1024px]:max-[1535px]:!space-y-1.5" : ""}`}>
        {(expanded ? list : list.slice(0, 4)).map((it, i) => (
          <div
            key={it.id ?? it.key ?? `${it.label}-${i}`}
            className={`group rounded-xl px-3 py-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors ${midRow}`}
          >
            {/* Viewport sm: is not card width — keep label + amount stacked so narrow columns never overlap */}
            <div className={`flex gap-2.5 ${compactMidDesktop ? "min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5" : ""}`}>
              <FaCheck className={`text-gold opacity-80 shrink-0 mt-0.5 text-sm ${compactMidDesktop ? "min-[768px]:max-[1023px]:!h-3 min-[1024px]:max-[1535px]:!h-3 min-[768px]:max-[1023px]:!w-3 min-[1024px]:max-[1535px]:!w-3" : ""}`} aria-hidden />
              <div className={`min-w-0 flex-1 space-y-1 ${compactMidDesktop ? "min-[768px]:max-[1023px]:!space-y-0.5 min-[1024px]:max-[1535px]:!space-y-0.5" : ""}`}>
                <span className={`text-gray-700 normal-case block text-sm leading-snug ${compactMidDesktop ? "min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs" : ""}`}>
                  {highlight(formatFeeListLabel(it.label ?? it.description), query)}
                </span>
                <span
                  title="Amount"
                  className={`block text-right text-sm font-semibold text-gray-800 tabular-nums group-hover:scale-[1.01] transition-transform ${compactMidDesktop ? "min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs" : ""}`}
                >
                  {formatAmount(it.amount)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className={`mt-5 text-right text-gray-600 ${midTotal}`}>
        <span className="text-xs mr-2">Total:</span>
        <span className="font-bold text-gray-800">{formatAmount(total)}</span>
      </div>
    </div>
  );
};

const CYBER_LEVEL_KEYS = ["l4", "l5", "l6"];

/** Same layout as main programme fee cards: 3× ProgramFeeBlock ($ icon, View breakdown, ✓ rows, Total). */
const CyberSecurityFeeSection = ({ data, query, expandedCyber, setExpandedCyber, academicYear }) => {
  if (!data) return null;
  const headers = data.columnHeaders || [];
  return (
    <section
      className="mt-4 min-[768px]:max-[1023px]:!mt-2 min-[1024px]:max-[1535px]:!mt-2 [font-family:var(--cms-font-family)]"
      style={{ fontFamily: "var(--cms-font-family)", fontSize: "var(--cms-font-size)" }}
      aria-labelledby="cyber-fee-heading"
    >
      <div className="w-full min-w-0 rounded-3xl border border-gray-200 bg-white p-8 shadow-lg md:p-12 xl:p-14 xl:shadow-xl min-[768px]:max-[1023px]:!rounded-xl min-[1024px]:max-[1535px]:!rounded-xl min-[768px]:max-[1023px]:!p-5 min-[1024px]:max-[1535px]:!p-5 min-[768px]:max-[1023px]:!shadow-md min-[1024px]:max-[1535px]:!shadow-md">
        <h3 id="cyber-fee-heading" className="mb-3 flex items-center gap-3 text-2xl font-bold normal-case text-gray-800 md:text-3xl min-[768px]:max-[1023px]:!mb-1.5 min-[1024px]:max-[1535px]:!mb-1.5 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5 min-[768px]:max-[1023px]:!text-lg min-[1024px]:max-[1535px]:!text-lg">
          <FaShieldAlt className="h-8 w-8 shrink-0 text-gold md:h-9 md:w-9 min-[768px]:max-[1023px]:!h-5 min-[1024px]:max-[1535px]:!h-5 min-[768px]:max-[1023px]:!w-5 min-[1024px]:max-[1535px]:!w-5" aria-hidden />
          {highlight(formatFeeCardTitle(data.title || "Cybersecurity Program Fee Structure"), query)}
        </h3>
        <p className="mb-8 max-w-3xl text-base text-gray-600 md:text-lg min-[768px]:max-[1023px]:!mb-4 min-[1024px]:max-[1535px]:!mb-4 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">
          {academicYear ? `Academic year ${academicYear}. ` : ""}
          Amounts in {CURRENCY}.
        </p>

        <div className="fee-structure-cyber-grid grid grid-cols-1 items-stretch gap-8 md:grid-cols-2 xl:grid-cols-3 xl:gap-10 min-[768px]:max-[1023px]:!gap-4 min-[1024px]:max-[1535px]:!gap-4">
          {CYBER_LEVEL_KEYS.map((levelKey, idx) => {
            const items = (data.rows || []).map((row) => ({
              id: `cyber-${levelKey}-${row.sno}-${idx}`,
              label: row.particulars,
              amount: row[levelKey],
            }));
            return (
              <div key={levelKey} className="transform md:hover:rotate-0 transition-transform h-full">
                <ProgramFeeBlock
                  title={headers[idx] || `NTA level ${idx + 1}`}
                  items={items}
                  total={data.totals?.[levelKey]}
                  expanded={!!expandedCyber[levelKey]}
                  onToggle={() =>
                    setExpandedCyber((s) => ({ ...s, [levelKey]: !s[levelKey] }))
                  }
                  query={query}
                  compactMidDesktop
                />
              </div>
            );
          })}
        </div>

        {Array.isArray(data.extras) && data.extras.length > 0 && (
          <div className="mt-8 space-y-3 min-[768px]:max-[1023px]:!mt-5 min-[1024px]:max-[1535px]:!mt-5 min-[768px]:max-[1023px]:!space-y-1.5 min-[1024px]:max-[1535px]:!space-y-1.5">
            <h4 className="mb-2 text-xl font-bold normal-case text-gray-800 min-[768px]:max-[1023px]:!mb-1 min-[1024px]:max-[1535px]:!mb-1 min-[768px]:max-[1023px]:!text-base min-[1024px]:max-[1535px]:!text-base">Additional fees</h4>
            <div className="space-y-3 min-[768px]:max-[1023px]:!space-y-1.5 min-[1024px]:max-[1535px]:!space-y-1.5">
              {data.extras.map((ex, i) => (
                <div
                  key={`${ex.label}-${i}`}
                  className="group rounded-2xl px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors min-[768px]:max-[1023px]:!rounded-lg min-[1024px]:max-[1535px]:!rounded-lg min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5"
                >
                  <div className="flex gap-3">
                    <FaCheck className="text-gold opacity-80 shrink-0 mt-0.5" aria-hidden />
                    <div className="min-w-0 flex-1 space-y-1.5 min-[768px]:max-[1023px]:!space-y-0.5 min-[1024px]:max-[1535px]:!space-y-0.5">
                      <span className="text-gray-700 normal-case block leading-snug min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">
                        {highlight(formatFeeListLabel(ex.label), query)}
                      </span>
                      <span className="block text-right font-semibold text-gray-800 tabular-nums min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">
                        {formatAmount(ex.amount)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.footnote && (
          <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-xl p-6 min-[768px]:max-[1023px]:!mt-5 min-[1024px]:max-[1535px]:!mt-5 min-[768px]:max-[1023px]:!rounded-md min-[1024px]:max-[1535px]:!rounded-md min-[768px]:max-[1023px]:!p-3 min-[1024px]:max-[1535px]:!p-3">
            <h5 className="font-bold text-yellow-800 mb-2 min-[768px]:max-[1023px]:!mb-1 min-[1024px]:max-[1535px]:!mb-1 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">📋 Note</h5>
            <p className="text-yellow-700 text-sm leading-relaxed min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px] min-[768px]:max-[1023px]:!leading-tight min-[1024px]:max-[1535px]:!leading-tight">{data.footnote}</p>
          </div>
        )}
      </div>
    </section>
  );
};

// Timeline item reserved for future use (custom timeline is rendered inline)

const FloatingSidebar = ({ query, onSearchChange, onClear, otherPayments = [] }) => (
  <aside id={sections.sidebar} className="sticky top-24 space-y-4 min-[768px]:max-[1023px]:!top-16 min-[1024px]:max-[1535px]:!top-16 min-[768px]:max-[1023px]:!space-y-2 min-[1024px]:max-[1535px]:!space-y-2" aria-label="Other payments">
    <div className="rounded-3xl p-6 bg-white border border-gray-200 shadow-lg min-[768px]:max-[1023px]:!rounded-xl min-[1024px]:max-[1535px]:!rounded-xl min-[768px]:max-[1023px]:!p-3 min-[1024px]:max-[1535px]:!p-3 min-[768px]:max-[1023px]:!shadow-sm min-[1024px]:max-[1535px]:!shadow-sm">
      <h3 className="text-gray-800 font-bold text-lg mb-4 flex items-center gap-2 min-[768px]:max-[1023px]:!mb-2 min-[1024px]:max-[1535px]:!mb-2 min-[768px]:max-[1023px]:!gap-1 min-[1024px]:max-[1535px]:!gap-1 min-[768px]:max-[1023px]:!text-sm min-[1024px]:max-[1535px]:!text-sm">
        <FaInfoCircle className="text-gold min-[768px]:max-[1023px]:!h-3.5 min-[1024px]:max-[1535px]:!h-3.5 min-[768px]:max-[1023px]:!w-3.5 min-[1024px]:max-[1535px]:!w-3.5" /> Other Payments
      </h3>
      <ul className="space-y-3 min-[768px]:max-[1023px]:!space-y-1.5 min-[1024px]:max-[1535px]:!space-y-1.5">
        {otherPayments.map((op) => (
          <li key={op.label} className="flex flex-col gap-1 text-gray-700 bg-gray-50 rounded-xl px-4 py-2 border border-gray-200 min-[768px]:max-[1023px]:!rounded-md min-[1024px]:max-[1535px]:!rounded-md min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1 min-[1024px]:max-[1535px]:!py-1 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">
            <span className="min-w-0 leading-snug">{highlight(op.label, query)}</span>
            <span className="self-end font-semibold tabular-nums min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">{formatAmount(op.amount)}</span>
          </li>
        ))}
      </ul>
    </div>

    <div className="rounded-3xl p-6 bg-white border border-gray-200 shadow-lg min-[768px]:max-[1023px]:!rounded-xl min-[1024px]:max-[1535px]:!rounded-xl min-[768px]:max-[1023px]:!p-3 min-[1024px]:max-[1535px]:!p-3 min-[768px]:max-[1023px]:!shadow-sm min-[1024px]:max-[1535px]:!shadow-sm">
      <h4 className="text-gray-800 font-bold mb-3 flex items-center gap-2 min-[768px]:max-[1023px]:!mb-1.5 min-[1024px]:max-[1535px]:!mb-1.5 min-[768px]:max-[1023px]:!gap-1 min-[1024px]:max-[1535px]:!gap-1 min-[768px]:max-[1023px]:!text-sm min-[1024px]:max-[1535px]:!text-sm">
        <FaSearch className="text-gold min-[768px]:max-[1023px]:!h-3.5 min-[1024px]:max-[1535px]:!h-3.5 min-[768px]:max-[1023px]:!w-3.5 min-[1024px]:max-[1535px]:!w-3.5" /> Quick Filter
      </h4>
      <div className="flex items-center gap-2 min-[768px]:max-[1023px]:!gap-1 min-[1024px]:max-[1535px]:!gap-1">
        <input
          type="text"
          value={query}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search fees, programs, schedule..."
          className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs min-[768px]:max-[1023px]:!rounded-md min-[1024px]:max-[1535px]:!rounded-md"
        />
        {query && (
          <button onClick={onClear} className="px-3 py-2 rounded-lg bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200 min-[768px]:max-[1023px]:!px-2 min-[1024px]:max-[1535px]:!px-2 min-[768px]:max-[1023px]:!py-1 min-[1024px]:max-[1535px]:!py-1 min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px]">Clear</button>
        )}
      </div>
    </div>
  </aside>
);

const FeeStructure = () => {
  const [feeData, setFeeData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState("");
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});
  const [expandedCyber, setExpandedCyber] = useState({ l4: false, l5: false, l6: false });
  const [active, setActive] = useState(sections.hero);
  const [schedulePulse, setSchedulePulse] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfPickerOpen, setPdfPickerOpen] = useState(false);
  /** Snapshot from CMS right before opening picker — avoids stale feeData if React hasn’t re-rendered yet */
  const [pdfPickerContext, setPdfPickerContext] = useState(null);
  const parallaxRef = useRef(null);
  const programsRef = useRef(null);
  const scheduleRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const fetchFeeStructure = async () => {
      try {
        const url = feeStructureApiUrl();
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error("Unable to load fee structure");
        const data = await res.json();
        const parsed = parseFeeStructurePayload(data);
        if (!parsed) {
          setDataError("Fee structure not available right now.");
          setFeeData(null);
          setDataLoading(false);
          return;
        }
        if (process.env.REACT_APP_DEBUG_FEE_PDF === "true") {
          console.log("[fee-structure] loaded from", url, {
            programmesPdf: getProgrammesPdfPath(parsed),
            cyberPdf: getCyberPdfPath(parsed),
            cmsOrigin: getCmsOriginForUploads(),
          });
        }
        setFeeData(parsed);
        const exp = {};
        (parsed.feePrograms || []).forEach((p) => {
          const key = p.id || p.title || `program-${Math.random()}`;
          exp[key] = false;
        });
        setExpanded(exp);
      } catch (err) {
        console.error("Failed to fetch fee structure:", err);
        setDataError("Fee structure not available right now.");
      } finally {
        setDataLoading(false);
      }
    };

    fetchFeeStructure();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!parallaxRef.current) return;
      const y = window.scrollY;
      parallaxRef.current.style.transform = `translateY(${y * 0.15}px)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Observe sections to toggle active quick link state
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px", threshold: 0.35 };
    const map = new Map([
      [sections.programs, programsRef],
      [sections.schedule, scheduleRef],
      [sections.sidebar, sidebarRef],
    ]);
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActive(entry.target.id);
      });
    }, opts);
    map.forEach((ref) => ref.current && obs.observe(ref.current));
    return () => obs.disconnect();
  }, []);

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const handlePrint = () => window.print();

  /** Fallback when CMS has not uploaded an official PDF: snapshot page via html2pdf (lower quality). */
  const downloadFeePageSnapshotPdf = async () => {
    const ensureLib = () =>
      new Promise((resolve, reject) => {
        if (window.html2pdf) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
        s.onload = () => resolve(true);
        s.onerror = reject;
        document.body.appendChild(s);
      });

    const fixPdfClone = (clonedDoc) => {
      const root = clonedDoc.getElementById("fee-structure-print-area");
      if (!root) return;
      const w = 1280;
      root.style.width = `${w}px`;
      root.style.maxWidth = `${w}px`;
      root.style.marginLeft = "auto";
      root.style.marginRight = "auto";
      root.style.boxSizing = "border-box";

      const mainRow = root.querySelector(".fee-structure-main-row");
      if (mainRow) {
        mainRow.style.display = "flex";
        mainRow.style.flexDirection = "row";
        mainRow.style.flexWrap = "nowrap";
        mainRow.style.alignItems = "flex-start";
        mainRow.style.gap = "1.5rem";
        mainRow.style.width = "100%";
      }
      const mainCol = root.querySelector(".fee-structure-main-column");
      if (mainCol) {
        mainCol.style.flex = "1 1 auto";
        mainCol.style.minWidth = "700px";
        mainCol.style.maxWidth = "calc(100% - 320px)";
      }

      const sidebar = clonedDoc.getElementById("fee-structure-sidebar");
      if (sidebar) {
        sidebar.style.transform = "none";
        sidebar.style.width = "300px";
        sidebar.style.maxWidth = "300px";
        sidebar.style.flexShrink = "0";
      }

      const programGrid = root.querySelector(".fee-structure-program-grid");
      if (programGrid) {
        programGrid.style.display = "grid";
        programGrid.style.gridTemplateColumns = "repeat(3, minmax(0, 1fr))";
        programGrid.style.gap = "1.5rem";
      }

      const scheduleGrid = root.querySelector(".fee-structure-schedule-grid");
      if (scheduleGrid) {
        scheduleGrid.style.display = "grid";
        scheduleGrid.style.gridTemplateColumns = "repeat(3, minmax(0, 1fr))";
        scheduleGrid.style.gap = "1.5rem";
      }

      const cyberGrid = root.querySelector(".fee-structure-cyber-grid");
      if (cyberGrid) {
        cyberGrid.style.display = "grid";
        cyberGrid.style.gridTemplateColumns = "repeat(3, minmax(0, 1fr))";
        cyberGrid.style.gap = "1.5rem";
      }

      root.querySelectorAll(".min-w-0:not(.fee-structure-main-column)").forEach((el) => {
        el.style.minWidth = "min(100%, 14rem)";
      });
    };

    await ensureLib();
    const target = document.getElementById("fee-structure-print-area");
    if (!target) return;

    const y = window.scrollY;
    window.scrollTo(0, 0);

    const academicSlug = (feeData?.academicYear || "2025-2026").replace(/\s+/g, "_");
    const opt = {
      margin: [8, 8, 8, 8],
      filename: `JR_Institute_Fee_Structure_${academicSlug}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 1280,
        windowHeight: target.scrollHeight,
        onclone: fixPdfClone,
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"], avoid: [".avoid-break", ".pdf-avoid-break"] },
    };

    await window.html2pdf().from(target).set(opt).save();
    window.scrollTo(0, y);
  };

  /** Pull latest fee data from CMS so PDF paths match what was just uploaded (avoids stale SPA state / browser cache). */
  const refreshFeeDataFromCms = async () => {
    try {
      const res = await fetch(feeStructureApiUrl(), { cache: "no-store" });
      if (!res.ok) return null;
      const data = await res.json();
      const parsed = parseFeeStructurePayload(data);
      if (!parsed) return null;
      setFeeData(parsed);
      setExpanded((prev) => {
        const next = {};
        (parsed.feePrograms || []).forEach((p) => {
          const key = p.id || p.title || `program-${Math.random()}`;
          next[key] = prev[key] ?? false;
        });
        return next;
      });
      return parsed;
    } catch (e) {
      console.warn("Could not refresh fee data from CMS:", e);
      return null;
    }
  };

  /** Download admin-uploaded PDF from CMS (`path` starts with /uploads/...) */
  const downloadOfficialPdf = async (relativePath, filename, updatedAt) => {
    const base = getCmsOriginForUploads().replace(/\/$/, "");
    const sep = relativePath.includes("?") ? "&" : "?";
    const v =
      updatedAt != null
        ? new Date(updatedAt).getTime()
        : Date.now();
    const fileUrl = `${base}${relativePath}${sep}v=${v}&r=${Date.now()}`;
    try {
      const res = await fetch(fileUrl, {
        mode: "cors",
        cache: "no-store",
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });
      if (res.ok) {
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        return;
      }
    } catch (e) {
      console.warn("Direct PDF fetch failed, opening in new tab:", e);
    }
    const sep2 = relativePath.includes("?") ? "&" : "?";
    window.open(`${base}${relativePath}${sep2}v=${v}&r=${Date.now()}`, "_blank", "noopener,noreferrer");
  };

  const closePdfPicker = () => {
    setPdfPickerOpen(false);
    setPdfPickerContext(null);
  };

  const handleDownload = async () => {
    const fresh = await refreshFeeDataFromCms();
    const src = fresh || feeData;
    const programmePath = getProgrammesPdfPath(src);
    const cyberPath = getCyberPdfPath(src);
    const hasProgrammes = Boolean(programmePath);
    const hasCyber = Boolean(cyberPath);

    if (process.env.REACT_APP_DEBUG_FEE_PDF === "true") {
      console.log("[fee-structure] handleDownload", {
        feeStructureApi: feeStructureApiUrl(),
        cmsOrigin: getCmsOriginForUploads(),
        programmePath,
        cyberPath,
      });
    }

    if (hasProgrammes || hasCyber) {
      setPdfPickerContext(src);
      setPdfPickerOpen(true);
      return;
    }

    console.warn(
      "[fee-structure] No CMS PDF URLs; using page snapshot (JR_Institute_Fee_Structure_*.pdf). Set programmes/cyber PDFs in CMS and ensure REACT_APP_CMS_BASE_URL matches the CMS where uploads live.",
      { programmePath, cyberPath }
    );

    try {
      await downloadFeePageSnapshotPdf();
    } catch (err) {
      console.error("PDF export failed:", err);
      window.print();
    }
  };

  const handleShare = async () => {
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: "JRIIT Fee Structure", url: window.location.href });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
    }
    setShareOpen(true);
  };

  // Smooth scroll helper (800ms)
  const scrollToSmooth = (el) => {
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
    const start = window.pageYOffset;
    const distance = top - start;
    const duration = 800;
    let startTime = null;
    const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const time = Math.min(1, (currentTime - startTime) / duration);
      const eased = easeInOutQuad(time);
      window.scrollTo(0, start + distance * eased);
      if (time < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  };

  const gotoPrograms = () => scrollToSmooth(programsRef.current);
  const gotoSchedule = () => { scrollToSmooth(scheduleRef.current); setSchedulePulse(true); setTimeout(()=>setSchedulePulse(false), 1200); };
  const gotoOtherPayments = () => { setSidebarOpen(true); scrollToSmooth(sidebarRef.current); };

  const programs = feeData?.feePrograms || [];
  const cyberFeeTable = useMemo(() => {
    try {
      const raw = feeData?.cyberSecurityFeeStructure;
      let o;
      if (raw != null && String(raw).trim() !== "") {
        o = typeof raw === "string" ? JSON.parse(raw) : raw;
      }
      if (o && o.enabled === false) return null;
      if (o && Array.isArray(o.rows) && o.rows.length > 0) return o;
      return DEFAULT_CYBER_FEE_STRUCTURE;
    } catch {
      return DEFAULT_CYBER_FEE_STRUCTURE;
    }
  }, [feeData]);
  const paymentSchedules = feeData?.paymentSchedules || [];
  const otherPayments = feeData?.otherPayments || [];
  const hostelDetails = feeData
    ? { sno: 1, detail: feeData.hostelDescription, amount: feeData.hostelAmount }
    : { sno: 1, detail: "Hostel details coming soon", amount: null };
  const bankingInstr = feeData?.bankingInstructions || "";
  const paymentTerms = feeData?.paymentTerms || "";
  const accountDetails = feeData
    ? { title: feeData.accountTitle, banks: feeData.accountDetails || [] }
    : { title: "", banks: [] };
  const foreignRemittance = feeData?.foreignRemittance || {};
  const contactPhones = feeData?.contactPhones || [];
  const phoneLinks = contactPhones.map((p) => ({ raw: p, href: `tel:${String(p).replace(/\s/g, "")}` }));

  const scheduleCardStyles = [
    "from-custom-pink/10 to-purple-100 border-custom-pink/20",
    "from-gold/10 to-yellow-100 border-gold/20",
    "from-indigo-100 to-blue-100 border-indigo-200",
  ];
  const remittanceValue = (value) => value || "-";

  if (dataLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center text-gray-700">Loading fee structure...</div>
        <Footer />
        <FloatingSocialPanels />
      </>
    );
  }

  if (dataError || !feeData) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center text-red-600">
          {dataError || "Fee structure not available"}
        </div>
        <Footer />
        <FloatingSocialPanels />
      </>
    );
  }

  return (
    <>
    <Navbar />
    <div className="min-h-screen bg-white text-gray-800 relative overflow-hidden">

      {/* Parallax floating accents */}
      <div ref={parallaxRef} className="pointer-events-none select-none">
        <div className="absolute -top-24 -left-24 w-[32rem] h-[32rem] rounded-full bg-custom-pink/20 blur-3xl" />
        <div className="absolute top-[40rem] -right-24 w-[28rem] h-[28rem] rounded-full bg-gold/10 blur-3xl" />
      </div>

      {/* Hero */}
      <section id={sections.hero} className="relative pt-28 pb-14 md:pt-32 md:pb-20 scroll-mt-32 min-[768px]:max-[1023px]:!pt-24 min-[1024px]:max-[1535px]:!pt-24 min-[768px]:max-[1023px]:!pb-10 min-[1024px]:max-[1535px]:!pb-10 min-[768px]:max-[1023px]:!scroll-mt-28 min-[1024px]:max-[1535px]:!scroll-mt-28">
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10">
          <nav className="flex items-center text-sm text-gray-600 gap-2 mb-6 min-[768px]:max-[1023px]:!mb-3 min-[1024px]:max-[1535px]:!mb-3 min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px] min-[768px]:max-[1023px]:!gap-1 min-[1024px]:max-[1535px]:!gap-1" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-gold flex items-center gap-2"><FaHome /> Home</Link>
            <FaChevronRight className="opacity-60" />
            <span className="text-gold">Fee Structure</span>
          </nav>

          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-5xl min-[768px]:max-[1023px]:!text-3xl min-[1024px]:max-[1535px]:!text-3xl min-[1280px]:max-[1810px]:!text-2xl min-[1280px]:max-[1810px]:leading-tight font-extrabold tracking-tight leading-tight text-gray-800 break-words">
              {feeData.heroTitle || "Fee Structure"}
            </h1>
            <p className="mt-3 text-gray-600 text-base md:text-lg min-[768px]:max-[1023px]:!mt-2 min-[1024px]:max-[1535px]:!mt-2 min-[768px]:max-[1023px]:!text-sm min-[1024px]:max-[1535px]:!text-sm min-[1280px]:max-[1810px]:text-base min-[1280px]:max-[1810px]:leading-relaxed">
              {feeData.heroDescription || "Explore program fees, payment timelines, hostel details, and official payment instructions."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3 no-print min-[768px]:max-[1023px]:!mt-3 min-[1024px]:max-[1535px]:!mt-3 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5 [&_button]:min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 [&_button]:min-[768px]:max-[1023px]:!py-1 min-[1024px]:max-[1535px]:!py-1 [&_button]:min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px] [&_a]:min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 [&_a]:min-[768px]:max-[1023px]:!py-1 min-[1024px]:max-[1535px]:!py-1 [&_a]:min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px]">
              <button onClick={gotoPrograms} className={`text-sm px-4 py-2 rounded-full border transition-colors ${active===sections.programs?"bg-custom-pink text-white border-custom-pink":"bg-gray-100 text-gray-700 border-gray-200 hover:bg-custom-pink hover:text-white"}`}>Programs</button>
              <button onClick={gotoSchedule} className={`text-sm px-4 py-2 rounded-full border transition-colors ${active===sections.schedule?"bg-custom-pink text-white border-custom-pink":"bg-gray-100 text-gray-700 border-gray-200 hover:bg-custom-pink hover:text-white"}`}>Schedule</button>
              <button onClick={gotoOtherPayments} className={`text-sm px-4 py-2 rounded-full border transition-colors ${active===sections.sidebar?"bg-custom-pink text-white border-custom-pink":"bg-gray-100 text-gray-700 border-gray-200 hover:bg-custom-pink hover:text-white"}`}>Other Payments</button>
              <a href="#hostel" className={`text-sm px-4 py-2 rounded-full border transition-colors ${active===sections.hostel?"bg-custom-pink text-white border-custom-pink":"bg-gray-100 text-gray-700 border-gray-200 hover:bg-custom-pink hover:text-white"}`}>Hostel</a>
              <a href="#payment" className={`text-sm px-4 py-2 rounded-full border transition-colors ${active===sections.payment?"bg-custom-pink text-white border-custom-pink":"bg-gray-100 text-gray-700 border-gray-200 hover:bg-custom-pink hover:text-white"}`}>Payment</a>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 no-print min-[768px]:max-[1023px]:!mt-3 min-[1024px]:max-[1535px]:!mt-3 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5 [&_button]:min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 [&_button]:min-[768px]:max-[1023px]:!py-1 min-[1024px]:max-[1535px]:!py-1 [&_button]:min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px]">
              <button onClick={handlePrint} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold text-white border border-gold hover:bg-yellow-500 min-[768px]:max-[1023px]:!gap-1 min-[1024px]:max-[1535px]:!gap-1"><FaPrint /> Print</button>
              <button onClick={handleDownload} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-custom-pink text-white border border-custom-pink hover:bg-purple-600 min-[768px]:max-[1023px]:!gap-1 min-[1024px]:max-[1535px]:!gap-1"><FaDownload /> Download PDF</button>
              <button onClick={handleShare} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 min-[768px]:max-[1023px]:!gap-1 min-[1024px]:max-[1535px]:!gap-1"><FaShareAlt /> Share</button>
            </div>
          </div>
        </div>
        <svg className="w-full h-14 md:h-20 min-[768px]:max-[1023px]:!h-12 min-[1024px]:max-[1535px]:!h-12 text-white" preserveAspectRatio="none" viewBox="0 0 100 100"><polygon fill="currentColor" points="0,100 100,0 100,100" /></svg>
      </section>

      {/* Main content */}
      <section className="relative" id={sections.programs} ref={programsRef}>
        <div className="container mx-auto px-4" id="fee-structure-print-area">
          {/* Programme fees title spans full width; cards + sidebar share a row below so Other Payments aligns with cards */}
          <div className="mb-6 min-[768px]:max-[1023px]:!mb-3 min-[1024px]:max-[1535px]:!mb-3">
            <h2
              id="programme-fees-heading"
              className="mb-2 flex flex-wrap items-center gap-3 text-2xl font-bold normal-case leading-snug text-gray-800 md:text-3xl min-[768px]:max-[1023px]:!mb-1 min-[1024px]:max-[1535px]:!mb-1 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5 min-[768px]:max-[1023px]:!text-lg min-[1024px]:max-[1535px]:!text-lg"
            >
              <FaUniversity className="h-7 w-7 shrink-0 text-gold min-[768px]:max-[1023px]:!h-5 min-[1024px]:max-[1535px]:!h-5 min-[768px]:max-[1023px]:!w-5 min-[1024px]:max-[1535px]:!w-5" aria-hidden />
              Programme fees — IT, Business Administration &amp; Travel &amp; Tourism
            </h2>
            <p className="max-w-4xl text-base leading-relaxed text-gray-600 md:text-lg min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs min-[768px]:max-[1023px]:!leading-snug min-[1024px]:max-[1535px]:!leading-snug">
              Tuition and standard fees for these three programmes. The three cards below are NTA Level 4 (Certificate),
              Level 5 (Technician Certificate), and Level 6 (Diploma). The Cybersecurity programme is listed separately
              under its own section.
            </p>
          </div>

          <div className="fee-structure-main-row flex flex-col gap-5 lg:flex-row lg:items-start min-[768px]:max-[1023px]:!gap-4 min-[1024px]:max-[1535px]:!gap-4">
            <div className="fee-structure-main-column min-w-0 flex-1 space-y-6 min-[768px]:max-[1023px]:!space-y-5 min-[1024px]:max-[1535px]:!space-y-5">
              {/* Program blocks from main table data with breakdown toggle */}
              <div
                className="fee-structure-program-grid grid grid-cols-1 items-stretch md:grid-cols-2 xl:grid-cols-3 gap-5 min-[768px]:max-[1023px]:!gap-4 min-[1024px]:max-[1535px]:!gap-4"
                aria-labelledby="programme-fees-heading"
              >
                {programs.map((program, idx) => {
                  const key = program.id || program.title || idx;
                  return (
                    <div key={key} className="h-full transform md:hover:rotate-0">
                      <ProgramFeeBlock
                        title={program.title}
                        items={program.items || []}
                        total={program.total}
                        expanded={expanded[key]}
                        onToggle={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}
                        query={query}
                        compactMidDesktop
                      />
                    </div>
                  );
                })}
              </div>

              {/* Payment schedule - Simplified */}
              <section id={sections.schedule} className="mt-4 min-[768px]:max-[1023px]:!mt-2 min-[1024px]:max-[1535px]:!mt-2" ref={scheduleRef}>
                <div className={`w-full min-w-0 rounded-2xl border border-gray-200 bg-white p-6 shadow-md transition-shadow md:p-10 xl:p-12 xl:shadow-lg min-[768px]:max-[1023px]:!rounded-xl min-[1024px]:max-[1535px]:!rounded-xl min-[768px]:max-[1023px]:!p-5 min-[1024px]:max-[1535px]:!p-5 min-[768px]:max-[1023px]:!shadow-md min-[1024px]:max-[1535px]:!shadow-md ${schedulePulse?"ring-2 ring-gold shadow-[0_0_0_6px_rgba(252,169,3,0.15)]":""}`}>
                  <h3 className="mb-3 flex items-center gap-3 text-2xl font-bold text-gray-800 md:text-3xl min-[768px]:max-[1023px]:!mb-1.5 min-[1024px]:max-[1535px]:!mb-1.5 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5 min-[768px]:max-[1023px]:!text-lg min-[1024px]:max-[1535px]:!text-lg"><FaCalendarAlt className="h-8 w-8 shrink-0 text-gold md:h-9 md:w-9 min-[768px]:max-[1023px]:!h-5 min-[1024px]:max-[1535px]:!h-5 min-[768px]:max-[1023px]:!w-5 min-[1024px]:max-[1535px]:!w-5" /> Payment Schedule</h3>
                  <p className="mb-8 max-w-3xl text-base text-gray-600 md:text-lg min-[768px]:max-[1023px]:!mb-4 min-[1024px]:max-[1535px]:!mb-4 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">Simple payment plans for each program level.</p>
                  
                  {/* Simplified Payment Cards */}
                  <div className="fee-structure-schedule-grid grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3 xl:gap-10 min-[768px]:max-[1023px]:!gap-4 min-[1024px]:max-[1535px]:!gap-4">
                    {paymentSchedules.map((schedule, idx) => (
                      <div
                        key={schedule.programId || idx}
                        className={`bg-gradient-to-br ${scheduleCardStyles[idx % scheduleCardStyles.length]} rounded-2xl border p-8 md:min-h-[13rem] min-[768px]:max-[1023px]:!rounded-lg min-[1024px]:max-[1535px]:!rounded-lg min-[768px]:max-[1023px]:!p-4 min-[1024px]:max-[1535px]:!p-4 min-[768px]:max-[1023px]:!min-h-0 min-[1024px]:max-[1535px]:!min-h-0`}
                      >
                        <h4 className="mb-5 text-lg font-bold text-gray-800 md:text-xl min-[768px]:max-[1023px]:!mb-2 min-[1024px]:max-[1535px]:!mb-2 min-[768px]:max-[1023px]:!text-sm min-[1024px]:max-[1535px]:!text-sm">{schedule.programTitle}</h4>
                      <div className="space-y-3 min-[768px]:max-[1023px]:!space-y-1.5 min-[1024px]:max-[1535px]:!space-y-1.5">
                          {(schedule.payments || []).map((payment, pIdx) => (
                            <div
                              key={`${schedule.programId}-${pIdx}`}
                              className="flex flex-col gap-1 border-b border-gray-200 py-2 min-[768px]:max-[1023px]:!py-1 min-[1024px]:max-[1535px]:!py-1"
                            >
                              <span className="min-w-0 text-gray-600 leading-snug min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">{highlight(payment.month, query)}</span>
                              <span className="self-end font-semibold text-gray-800 tabular-nums min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">
                                {formatAmount(payment.amount)}
                              </span>
                        </div>
                          ))}
                        <div className="mt-4 pt-3 border-t-2 border-custom-pink min-[768px]:max-[1023px]:!mt-2 min-[1024px]:max-[1535px]:!mt-2 min-[768px]:max-[1023px]:!pt-1.5 min-[1024px]:max-[1535px]:!pt-1.5">
                          <div className="flex flex-col gap-1">
                            <span className="font-bold text-gray-800 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">Total</span>
                              <span className="self-end font-bold text-custom-pink text-lg tabular-nums min-[768px]:max-[1023px]:!text-sm min-[1024px]:max-[1535px]:!text-sm">{formatAmount(schedule.total)}</span>
                        </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Important Notes */}
                  <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-xl p-6 min-[768px]:max-[1023px]:!mt-5 min-[1024px]:max-[1535px]:!mt-5 min-[768px]:max-[1023px]:!rounded-md min-[1024px]:max-[1535px]:!rounded-md min-[768px]:max-[1023px]:!p-3 min-[1024px]:max-[1535px]:!p-3">
                    <h5 className="font-bold text-yellow-800 mb-2 min-[768px]:max-[1023px]:!mb-1 min-[1024px]:max-[1535px]:!mb-1 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">📋 Important Payment Information</h5>
                    <ul className="text-yellow-700 space-y-1 text-sm min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px] min-[768px]:max-[1023px]:!space-y-0 min-[1024px]:max-[1535px]:!space-y-0">
                      <li>• All payments must be made by the 5th of each month</li>
                      <li>• Late payments incur a 2% penalty per month</li>
                      <li>• Installment plans available upon written request</li>
                      <li>• Keep all payment receipts for your records</li>
                    </ul>
                  </div>
                </div>
              </section>

              {cyberFeeTable && (
                <CyberSecurityFeeSection
                  data={cyberFeeTable}
                  query={query}
                  expandedCyber={expandedCyber}
                  setExpandedCyber={setExpandedCyber}
                  academicYear={feeData.academicYear}
                />
              )}

              {/* Hostel details */}
              <section id={sections.hostel} className="relative">
                <div className="rounded-3xl p-6 md:p-8 bg-white border border-gray-200 shadow-lg min-[768px]:max-[1023px]:!rounded-xl min-[1024px]:max-[1535px]:!rounded-xl min-[768px]:max-[1023px]:!p-4 min-[1024px]:max-[1535px]:!p-4 min-[768px]:max-[1023px]:!shadow-sm min-[1024px]:max-[1535px]:!shadow-sm">
                  <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3 mb-4 min-[768px]:max-[1023px]:!mb-2 min-[1024px]:max-[1535px]:!mb-2 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5 min-[768px]:max-[1023px]:!text-lg min-[1024px]:max-[1535px]:!text-lg"><FaBed className="text-gold min-[768px]:max-[1023px]:!h-4 min-[1024px]:max-[1535px]:!h-4 min-[768px]:max-[1023px]:!w-4 min-[1024px]:max-[1535px]:!w-4" /> Hostel Details</h3>
                  <div className="space-y-3 text-gray-600 min-[768px]:max-[1023px]:!space-y-1.5 min-[1024px]:max-[1535px]:!space-y-1.5 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">
                    <div className="flex flex-col gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 min-[768px]:max-[1023px]:!rounded-md min-[1024px]:max-[1535px]:!rounded-md min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5 min-[768px]:max-[1023px]:!gap-1 min-[1024px]:max-[1535px]:!gap-1">
                      <div className="flex min-w-0 flex-wrap items-center gap-3 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5">
                        <span className="text-gray-500 min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px]">S/NO: {hostelDetails.sno}</span>
                        <span className="font-semibold text-gray-800 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">{highlight(hostelDetails.detail, query)}</span>
                      </div>
                      <span className="self-end font-semibold text-gray-800 tabular-nums min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">
                        {formatAmount(hostelDetails.amount)}
                        <span className="text-gray-500 text-xs"> / academic year</span>
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Payment instructions + bank details */}
              <section id={sections.payment} className="relative">
                <div className="rounded-3xl p-6 md:p-8 bg-white border border-gray-200 shadow-lg min-[768px]:max-[1023px]:!rounded-xl min-[1024px]:max-[1535px]:!rounded-xl min-[768px]:max-[1023px]:!p-4 min-[1024px]:max-[1535px]:!p-4 min-[768px]:max-[1023px]:!shadow-sm min-[1024px]:max-[1535px]:!shadow-sm">
                  <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3 mb-4 min-[768px]:max-[1023px]:!mb-2 min-[1024px]:max-[1535px]:!mb-2 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5 min-[768px]:max-[1023px]:!text-lg min-[1024px]:max-[1535px]:!text-lg"><FaUniversity className="text-gold min-[768px]:max-[1023px]:!h-4 min-[1024px]:max-[1535px]:!h-4 min-[768px]:max-[1023px]:!w-4 min-[1024px]:max-[1535px]:!w-4" /> Payment Instructions</h3>
                  <p className="text-gray-600 mb-4 min-[768px]:max-[1023px]:!mb-2 min-[1024px]:max-[1535px]:!mb-2 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">{bankingInstr}</p>
                  <p className="text-gray-600 whitespace-pre-line min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs min-[768px]:max-[1023px]:!leading-snug min-[1024px]:max-[1535px]:!leading-snug">{paymentTerms}</p>

                  <div className="mt-6 rounded-2xl p-4 bg-gray-50 border border-gray-200 min-[768px]:max-[1023px]:!mt-3 min-[1024px]:max-[1535px]:!mt-3 min-[768px]:max-[1023px]:!rounded-lg min-[1024px]:max-[1535px]:!rounded-lg min-[768px]:max-[1023px]:!p-2.5 min-[1024px]:max-[1535px]:!p-2.5">
                    <div className="flex items-start justify-between gap-4 min-[768px]:max-[1023px]:!gap-2 min-[1024px]:max-[1535px]:!gap-2">
                      <div className="min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">
                        <p className="text-gray-800 font-bold min-[768px]:max-[1023px]:!text-sm min-[1024px]:max-[1535px]:!text-sm">{accountDetails.title}</p>
                        {accountDetails.banks.map((b) => (
                          <p key={b.label} className="text-gray-600 min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px] min-[768px]:max-[1023px]:!leading-tight min-[1024px]:max-[1535px]:!leading-tight"><span className="font-semibold">{b.label}:</span> {b.value}</p>
                        ))}
                      </div>
                      <button onClick={() => copyText(`${accountDetails.title}\n${accountDetails.banks.map(b=>`${b.label}: ${b.value}`).join("\n")}`)} className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-custom-pink text-white border border-custom-pink hover:bg-purple-600 transition-colors min-[768px]:max-[1023px]:!gap-1 min-[1024px]:max-[1535px]:!gap-1 min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1 min-[1024px]:max-[1535px]:!py-1 min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px]"><FaCopy /> {copied ? "Copied!" : "Copy"}</button>
                    </div>
                  </div>

                  <div className="mt-6 rounded-2xl p-4 bg-gray-50 border border-gray-200 min-[768px]:max-[1023px]:!mt-3 min-[1024px]:max-[1535px]:!mt-3 min-[768px]:max-[1023px]:!rounded-lg min-[1024px]:max-[1535px]:!rounded-lg min-[768px]:max-[1023px]:!p-2.5 min-[1024px]:max-[1535px]:!p-2.5">
                    <h4 className="font-semibold mb-2 text-gray-800 min-[768px]:max-[1023px]:!mb-1 min-[1024px]:max-[1535px]:!mb-1 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">Foreign Remittance Details</h4>
                    <div className="grid sm:grid-cols-2 gap-2 text-gray-600 min-[768px]:max-[1023px]:!gap-1 min-[1024px]:max-[1535px]:!gap-1 min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px]">
                      <p><span className="font-semibold">Bank:</span> {remittanceValue(foreignRemittance.bank)}</p>
                      <p><span className="font-semibold">Account Name:</span> {remittanceValue(foreignRemittance.name)}</p>
                      <p><span className="font-semibold">Account Type:</span> {remittanceValue(foreignRemittance.type)}</p>
                      <p><span className="font-semibold">Account #:</span> {remittanceValue(foreignRemittance.number)}</p>
                      <p><span className="font-semibold">Correspondent Bank:</span> {remittanceValue(foreignRemittance.correspondent)}</p>
                      <p><span className="font-semibold">SWIFT:</span> {remittanceValue(foreignRemittance.swift)}</p>
                      <p><span className="font-semibold">ABA Number:</span> {remittanceValue(foreignRemittance.aba)}</p>
                      <p><span className="font-semibold">For Credit of USD:</span> {remittanceValue(foreignRemittance.credit)}</p>
                      <p><span className="font-semibold">D T Bank Swift:</span> {remittanceValue(foreignRemittance.dtSwift)}</p>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Floating sidebar — top aligns with programme fee cards (heading is outside this row) */}
            <div
              id="fee-structure-sidebar"
              className="w-full shrink-0 lg:w-80 lg:max-w-sm lg:self-start"
              ref={sidebarRef}
            >
              <div className={`transition-transform duration-500 ${sidebarOpen?"translate-x-0":"translate-x-96 lg:translate-x-0"}`}>
                <FloatingSidebar query={query} onSearchChange={setQuery} onClear={() => setQuery("")} otherPayments={otherPayments} />
              </div>
              <div className="mt-3 lg:hidden no-print">
                <button onClick={()=>setSidebarOpen((o)=>!o)} className="w-full px-4 py-2 rounded-xl bg-custom-pink text-white border border-custom-pink hover:bg-purple-600">{sidebarOpen?"Hide Other Payments":"Show Other Payments"}</button>
              </div>
            </div>
          </div>
        </div>
        <svg className="mt-16 w-full h-16 md:h-24 min-[768px]:max-[1023px]:!mt-10 min-[1024px]:max-[1535px]:!mt-10 min-[768px]:max-[1023px]:!h-12 min-[1024px]:max-[1535px]:!h-12 text-white rotate-180" preserveAspectRatio="none" viewBox="0 0 100 100"><polygon fill="currentColor" points="0,100 100,0 100,100" /></svg>
      </section>

      {/* Contact footer */}
      <section id={sections.contact} className="py-12 bg-gray-50 min-[768px]:max-[1023px]:!py-10 min-[1024px]:max-[1535px]:!py-10">
        <div className="container mx-auto px-4">
          <div className="rounded-3xl p-6 md:p-10 bg-white border border-gray-200 shadow-lg min-[768px]:max-[1023px]:!rounded-xl min-[1024px]:max-[1535px]:!rounded-xl min-[768px]:max-[1023px]:!p-5 min-[1024px]:max-[1535px]:!p-5 min-[768px]:max-[1023px]:!shadow-sm min-[1024px]:max-[1535px]:!shadow-sm">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 min-[768px]:max-[1023px]:!gap-3 min-[1024px]:max-[1535px]:!gap-3">
              <div className="min-[768px]:max-[1023px]:!max-w-lg min-[1024px]:max-[1535px]:!max-w-lg">
                <h3 className="text-2xl font-bold text-gray-800 min-[768px]:max-[1023px]:!text-lg min-[1024px]:max-[1535px]:!text-lg">Need assistance?</h3>
                <p className="text-gray-600 mt-2 min-[768px]:max-[1023px]:!mt-1.5 min-[1024px]:max-[1535px]:!mt-1.5 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs min-[768px]:max-[1023px]:!leading-snug min-[1024px]:max-[1535px]:!leading-snug">Contact our admissions and finance teams for any questions about fees and payments.</p>
                <div className="mt-3 flex flex-wrap gap-3 min-[768px]:max-[1023px]:!mt-1.5 min-[1024px]:max-[1535px]:!mt-1.5 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">
                  {phoneLinks.map((ph) => (
                    <a key={ph.href} href={ph.href} className="underline text-gold hover:opacity-90">{ph.raw}</a>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-3 no-print min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5">
                <Link to="/contact" className="px-6 py-3 rounded-full bg-gold text-black font-semibold hover:brightness-110 transition min-[768px]:max-[1023px]:!px-3 min-[1024px]:max-[1535px]:!px-3 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">Contact Us</Link>
                <a href="#hero" className="px-6 py-3 rounded-full bg-custom-pink text-white border border-custom-pink hover:bg-purple-600 transition min-[768px]:max-[1023px]:!px-3 min-[1024px]:max-[1535px]:!px-3 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">Back to top</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Desktop Share Modal */}
      {shareOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 no-print" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-2xl bg-[#111827] border border-white/10 p-6 shadow-2xl min-[768px]:max-[1023px]:!max-w-[16rem] min-[1024px]:max-[1535px]:!max-w-[16rem] min-[768px]:max-[1023px]:!rounded-lg min-[1024px]:max-[1535px]:!rounded-lg min-[768px]:max-[1023px]:!p-3 min-[1024px]:max-[1535px]:!p-3">
            <div className="flex items-center justify-between mb-4 min-[768px]:max-[1023px]:!mb-2 min-[1024px]:max-[1535px]:!mb-2">
              <h4 className="text-white font-semibold min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">Share Fee Structure</h4>
              <button onClick={()=>setShareOpen(false)} className="text-white/70 hover:text-white min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">✕</button>
            </div>
            <div className="space-y-3 min-[768px]:max-[1023px]:!space-y-1.5 min-[1024px]:max-[1535px]:!space-y-1.5">
              <button onClick={()=>copyText(window.location.href)} className="w-full text-left px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white hover:bg-white/20 min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">Copy Link</button>
              <a href={`mailto:?subject=JRIIT Fee Structure&body=${encodeURIComponent(window.location.href)}`} className="block px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white hover:bg-white/20 min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">Share via Email</a>
              <a href={`https://wa.me/?text=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noreferrer" className="block px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white hover:bg-white/20 min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">Share via WhatsApp</a>
            </div>
            <div className="mt-4 text-right min-[768px]:max-[1023px]:!mt-2 min-[1024px]:max-[1535px]:!mt-2">
              <button onClick={()=>setShareOpen(false)} className="px-4 py-2 rounded-full bg-white/10 border border-white/10 text-white hover:bg-white/20 min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1 min-[1024px]:max-[1535px]:!py-1 min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px]">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Choose which official PDF to download (any time CMS has at least one uploaded PDF) */}
      {pdfPickerOpen && pdfPickerContext && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 no-print p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pdf-picker-title"
        >
          <div className="w-full max-w-md rounded-2xl bg-white border border-gray-200 p-6 shadow-2xl min-[768px]:max-[1023px]:!max-w-xs min-[1024px]:max-[1535px]:!max-w-xs min-[768px]:max-[1023px]:!rounded-lg min-[1024px]:max-[1535px]:!rounded-lg min-[768px]:max-[1023px]:!p-3 min-[1024px]:max-[1535px]:!p-3">
            <div className="flex items-center justify-between mb-4 min-[768px]:max-[1023px]:!mb-2 min-[1024px]:max-[1535px]:!mb-2">
              <h4 id="pdf-picker-title" className="text-lg font-bold text-gray-900 min-[768px]:max-[1023px]:!text-sm min-[1024px]:max-[1535px]:!text-sm">Download fee structure PDF</h4>
              <button type="button" onClick={closePdfPicker} className="text-gray-500 hover:text-gray-800 text-xl leading-none min-[768px]:max-[1023px]:!text-base min-[1024px]:max-[1535px]:!text-base" aria-label="Close">✕</button>
            </div>
            <p className="text-sm text-gray-600 mb-4 min-[768px]:max-[1023px]:!mb-2 min-[1024px]:max-[1535px]:!mb-2 min-[768px]:max-[1023px]:!text-[11px] min-[1024px]:max-[1535px]:!text-[11px]">Choose IT / DBA / Travel &amp; Tourism, or Cyber Security (upload each file in the CMS).</p>
            <div className="flex flex-col gap-3 min-[768px]:max-[1023px]:!gap-1.5 min-[1024px]:max-[1535px]:!gap-1.5">
              {(() => {
                const ctx = pdfPickerContext;
                const canP = Boolean(getProgrammesPdfPath(ctx));
                const canC = Boolean(getCyberPdfPath(ctx));
                return (
                  <>
                    <button
                      type="button"
                      disabled={!canP}
                      className={`group w-full text-left px-4 py-3 rounded-xl border transition-colors min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5 min-[768px]:max-[1023px]:!rounded-md min-[1024px]:max-[1535px]:!rounded-md ${
                        canP
                          ? "border-gray-200 bg-gray-50 hover:bg-custom-pink hover:border-custom-pink hover:text-white"
                          : "cursor-not-allowed border-gray-100 bg-gray-100 opacity-60"
                      }`}
                      onClick={async () => {
                        if (!canP) return;
                        const updated = await refreshFeeDataFromCms();
                        const src = updated || ctx;
                        const path = getProgrammesPdfPath(src);
                        const sl = (src.academicYear || "2025-2026").replace(/\s+/g, "_");
                        closePdfPicker();
                        if (path) await downloadOfficialPdf(path, `JR_Fee_Structure_Programmes_${sl}.pdf`, src.updatedAt);
                      }}
                    >
                      <span className={`font-semibold block ${canP ? "text-gray-900 group-hover:text-white" : "text-gray-500"}`}>
                        IT, Business Administration &amp; Travel &amp; Tourism
                      </span>
                      <span className={`text-xs ${canP ? "text-gray-600 group-hover:text-white/90" : "text-gray-400"}`}>
                        {canP ? "Official programme fees PDF" : "Not uploaded — add “Programmes” PDF in CMS"}
                      </span>
                    </button>
                    <button
                      type="button"
                      disabled={!canC}
                      className={`group w-full text-left px-4 py-3 rounded-xl border transition-colors min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1.5 min-[1024px]:max-[1535px]:!py-1.5 min-[768px]:max-[1023px]:!rounded-md min-[1024px]:max-[1535px]:!rounded-md ${
                        canC
                          ? "border-gray-200 bg-gray-50 hover:bg-custom-pink hover:border-custom-pink hover:text-white"
                          : "cursor-not-allowed border-gray-100 bg-gray-100 opacity-60"
                      }`}
                      onClick={async () => {
                        if (!canC) return;
                        const updated = await refreshFeeDataFromCms();
                        const src = updated || ctx;
                        const path = getCyberPdfPath(src);
                        const sl = (src.academicYear || "2025-2026").replace(/\s+/g, "_");
                        closePdfPicker();
                        if (path) await downloadOfficialPdf(path, `JR_Fee_Structure_Cyber_Security_${sl}.pdf`, src.updatedAt);
                      }}
                    >
                      <span className={`font-semibold block ${canC ? "text-gray-900 group-hover:text-white" : "text-gray-500"}`}>
                        Cyber Security
                      </span>
                      <span className={`text-xs ${canC ? "text-gray-600 group-hover:text-white/90" : "text-gray-400"}`}>
                        {canC ? "Official cyber programme PDF" : "Not uploaded — add “Cyber Security” PDF in CMS"}
                      </span>
                    </button>
                  </>
                );
              })()}
            </div>
            <div className="mt-4 text-right min-[768px]:max-[1023px]:!mt-2 min-[1024px]:max-[1535px]:!mt-2">
              <button type="button" onClick={closePdfPicker} className="px-4 py-2 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-100 min-[768px]:max-[1023px]:!px-2.5 min-[1024px]:max-[1535px]:!px-2.5 min-[768px]:max-[1023px]:!py-1 min-[1024px]:max-[1535px]:!py-1 min-[768px]:max-[1023px]:!text-xs min-[1024px]:max-[1535px]:!text-xs">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          header, nav { display: none !important; }
          body { background: #ffffff !important; }
          #fee-structure-print-area { page-break-inside: avoid; }
        }
      `}</style>

      <Footer />
      <FloatingSocialPanels />
    </div>
    </>
  );
};

export default FeeStructure;
